<template>
  <span>
    <span 
      v-if="isShippingItem" 
      class="shipping-status-badge"
      :class="[statusInfo.class, { [collectedClass]: isClientPendingSettled }]"
      :title="badgeTitle"
    >
      {{ $t(statusInfo.label) }}

    </span>
    <span v-else class="shipping-status-badge status-grey">
      {{ $t('N/A') }}
    </span>
  </span>
</template>

<script>
import {
  getShippingStatusInfo,
  isShippingClientPendingSettled,
  SHIPPING_COLLECTED_CLASS,
} from '@/utils/shippingStatus';
import { isDuntowerTenant } from '@/utils/dynamicModelValidationSwal';

export default {
  name: 'ShippingStatusBadge',
  props: {
    item: {
      type: Object,
      required: true,
    },
    modelName: {
      type: String,
      default: 'shipping',
    },
    showDescription: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: 'normal', // 'small', 'normal', 'large'
      validator: value => ['small', 'normal', 'large'].includes(value)
    }
  },
  computed: {
    isShippingItem() {
      return this.modelName === 'shipping' && this.item && typeof this.item === 'object';
    },
    statusInfo() {
      if (!this.isShippingItem) {
        return {
          class: 'status-grey',
          label: this.$t('N/A'),
          description: this.$t('Not a shipping item')
        };
      }
      
      // Pasar la función de traducción de manera segura
      const t = this.$t || ((key) => key);
      return getShippingStatusInfo(this.item, t);
    },
    collectedClass() {
      return SHIPPING_COLLECTED_CLASS;
    },
    /**
     * Ticket #424: en Duntower el texto del badge va en verde cuando el cliente ya
     * pagó todo, para distinguir a quién hay que reclamarle el cobro. Es el mismo
     * badge del reporte «Shipping» y del panel del día del dashboard.
     */
    isClientPendingSettled() {
      return (
        this.isShippingItem &&
        isDuntowerTenant() &&
        isShippingClientPendingSettled(this.item)
      );
    },
    badgeTitle() {
      const description = this.statusInfo.description || '';
      if (!this.isClientPendingSettled) return description;
      const collected = this.$t('Fully collected');
      return description ? `${description} — ${collected}` : collected;
    }
  }
};
</script>

<style scoped>
.shipping-status-badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  text-align: center;
  min-width: 60px;
  transition: all 0.2s ease;
}

/* Tamaños */
.shipping-status-badge.small {
  padding: 2px 6px;
  font-size: 10px;
  min-width: 50px;
}

.shipping-status-badge.large {
  padding: 6px 12px;
  font-size: 14px;
  min-width: 80px;
}

/* Colores de status */
.status-grey {
  background-color: #6c757d;
  color: white;
}

.status-yellow {
  background-color: #ffc107;
  color: #212529;
}

.status-orange {
  background-color: #fd7e14;
  color: white;
}

.status-blue {
  background-color: #0d6efd;
  color: white;
}

.status-red {
  background-color: #dc3545;
  color: white;
}

.status-green {
  background-color: #4ddf4d;
  color: white;
}

/* Efecto hover */
.shipping-status-badge:hover {
  opacity: 0.8;
  transform: scale(1.05);
}

/* Efecto de pulso para status críticos */
.status-red {
  animation: pulse-red 2s infinite;
}

@keyframes pulse-red {
  0% { opacity: 1; }
  50% { opacity: 0.7; }
  100% { opacity: 1; }
}
</style>
